import Component from '../core/Component'
import { queries } from '../core/config'
import Scroll, { disableScroll, enableScroll } from '../services/Scroll'
import throttle from 'lodash/throttle'

const STATES = {
    MAP_OPEN: 'is-map-open'
}

export default class Mapick extends Component {
    constructor(element, options = {}) {
        super(element, options = {
            openMapOnInit: false
        })

        this.ref = {
            map: null,
            mapContainer: null,
            list: null,
            listItemTemplate: null,
            bubble: null,
            bubbleItemTemplate: null,
            jsonData: null,
            togglers: [],
        }

        this.jsonData = this.normalizeJsonData(JSON.parse(this.ref.jsonData.innerText ?? '{}'))
        this.observer = new IntersectionObserver(this.handleIntersection, {
            threshold: 0,
        })
        this.cache = {}
    }

    get points() {
        if (!this.cache.points) {
            this.cache.points = this.jsonData.points
        }
        return this.cache.points
    }

    prepare() {
        if (this.ref.map && this.ref.mapContainer) {
            this.sticky = createSticky({
                containerEl: this.ref.mapContainer,
                element: this.ref.map,
            })
            this.sticky.watch()
        }
        if (this.ref.map) {
            this.observer.observe(this.ref.map)
        }
        if (this.ref.list) {
            this.observer.observe(this.ref.list)
        }
        if (this.ref.bubble) {
            this.observer.observe(this.ref.bubble)
        }
        this.ref.togglers.forEach(toggler => toggler.addEventListener('click', this.handleTogglerClick))
        if (this.options.openMapOnInit) {
            this.toggleMap()
        }
    }

    destroy() {
        if (this.observer) {
            this.observer.unobserve(this.ref.map)
            this.observer.unobserve(this.ref.list)
            this.observer.unobserve(this.ref.bubble)
        }
    }

    handleTogglerClick = () => {
        this.toggleMap()
    }

    toggleMap() {
        if (window.matchMedia(queries.largeUp).matches) {
            return
        }

        const isOpen = this.element.classList.toggle(STATES.MAP_OPEN)
        if (isOpen) {
            disableScroll()
        } else {
            enableScroll()
        }
    }

    handleIntersection = (entries) => {
        entries.forEach((entry) => {
            if (entry.target === this.ref.map && entry.isIntersecting) {
                this.initializeMap()
                this.observer.unobserve(this.ref.map)
            }
            if (entry.target === this.ref.list && entry.isIntersecting) {
                this.initializeList()
                this.observer.unobserve(this.ref.list)
            }
            if (entry.target === this.ref.bubble && entry.isIntersecting) {
                this.initializeBubble()
                this.observer.unobserve(this.ref.bubble)
            }
        })
    }

    async initializeMap() {
        if (!this.ref.map || this.map) {
            return
        }

        const mod = await import('./Map.js')
        const Map = mod.default
        const MAP_EMITS = mod.EMITS
        this.map = new Map(this.ref.map, {
            maxZoomOnBounds: 11,
        })

        const handleMapMarkerClick = (event) => {
            const { marker, pointId } = event

            this.map.markers.forEach(_marker => {
                if (_marker === marker) {
                    _marker.setState('active', true)

                    if (this.bubble) {
                        const point = this.points.find(point => point.id === pointId)
                        this.bubble.show([point])
                    }
                    if (this.list) {
                        const point = this.points.find(point => point.id === pointId)
                        this.list.scrollToItem(point)
                        this.points.forEach(_point => {
                            this.list.setItemState(_point, 'active', _point === point)
                        })
                    }
                } else {
                    _marker.setState('active', false)
                }
            })
        }

        const handleMarkerMouseEnter = (event) => {
            const { pointId, marker } = event
            this.map.markers.forEach(_marker => {
                _marker.setState('hover', _marker === marker)
            })
            if (this.list) {
                const point = this.points.find(point => point.id === pointId)
                this.list.setItemState(point, 'hover', true)
            }
        }
        const handleMarkerMouseLeave = (event) => {
            const { pointId, marker } = event
            marker.setState('hover', false)
            if (this.list) {
                const point = this.points.find(point => point.id === pointId)
                this.list.setItemState(point, 'hover', false)
            }
        }
        let isFirstRender = true
        const handleThrottledMapBoundsChanged = throttle(() => {
            if (!isFirstRender) {
                this.map.markers.forEach(marker => marker.setState('visible', true))
            }

            isFirstRender = false

            if (this.list) {
                const mapBounds = this.map.map.getBounds()
                const pointIds = this.map.markers.filter(marker => marker.getVisible() && mapBounds.contains(marker.getPosition())).map(marker => marker.options.pointId)
                const sortedVisiblePoints = this.points.filter(point => pointIds.includes(point.id)).sort((a,b) => a.order - b.order)
                this.list.show(sortedVisiblePoints)
            }

            //zabranit tomu, ze scroll skace... kdyz se hybe z mapou, tak muzou mizet ze seznamu polozky a pozice scrollu zustane stejne => takze mapa odskace klidne mimo viewport
            if (this.sticky) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.sticky.resize()
                        const { documentEnd, positionOffset } = this.sticky.getState()
                        if (document.documentElement.scrollTop > documentEnd + positionOffset) {
                            document.documentElement.scrollTo({
                                top: documentEnd + positionOffset
                            })
                        }
                    })
                })
            }
        },500)

        this.map.on(MAP_EMITS.MAP_BOUNDSCHANGED, handleThrottledMapBoundsChanged)
        this.map.on(MAP_EMITS.MARKER_MOUSEENTER, handleMarkerMouseEnter)
        this.map.on(MAP_EMITS.MARKER_MOUSELEAVE, handleMarkerMouseLeave)
        this.map.on(MAP_EMITS.MARKER_CLICK, handleMapMarkerClick)
        await this.map.prepare()
        await this.map.show(this.points)
        let bounds = null
        if (this.jsonData.visiblePointIds) {
            const visibleMarkers = this.map.markers.filter(marker => {
                const isVisible = this.jsonData.visiblePointIds.includes(marker.options.pointId)
                marker.setState('visible', isVisible)
                return isVisible
            })
            if (visibleMarkers.length) {
                bounds = this.map.getBoundsForMarkers(visibleMarkers)
            }
        }
        await this.map.fit(bounds, { top: 40, left: 40, right: 40, bottom: 40 })
    }

    async initializeList() {
        if (!this.ref.list || this.list) {
            return
        }
        const mod = await import('./MapickList.js')
        const MapickList = mod.default
        const MAPICKLIST_EMITS = mod.EMITS
        this.list = new MapickList(this.ref.list, {
            itemTemplate: this.ref.listItemTemplate.innerText.trim()
        })
        const handleItemHover = (event) => {
            const { pointId } = event
            if (this.map) {
                const marker = this.map.findMarker(pointId)
                this.map.markers.forEach(_marker => {
                    _marker.setState('hover', marker === _marker)
                })
            }
        }
        const handleUnhover = () => {
            if (this.map) {
                this.map.markers.forEach(marker => marker.setState('hover', false))
            }
        }
        this.list.on(MAPICKLIST_EMITS.ITEM_HOVER, handleItemHover)
        this.list.on(MAPICKLIST_EMITS.UNHOVER, handleUnhover)
        await this.list.prepare()
        const visiblePoints = this.jsonData.visiblePointIds ? this.points.filter(point => this.jsonData.visiblePointIds.includes(point.id)) : this.points
        const ordererVisiblePoints = visiblePoints.sort((a,b) => a.order - b.order)
        this.list.show(ordererVisiblePoints)
    }

    async initializeBubble() {
        if (!this.ref.bubble || this.bubble) {
            return
        }
        const mod = await import('./MapickBubble.js')
        const MapickBubble = mod.default
        this.bubble = new MapickBubble(this.ref.bubble, {
            itemTemplate: this.ref.bubbleItemTemplate.innerText.trim()
        })
        await this.bubble.prepare()
    }

    normalizeJsonData(jsonData) {
        if (!jsonData.points) {
            jsonData.points = []
        }
        jsonData.points.forEach(point => {
            point.id = `${point.id}`
        })
        if (jsonData.visiblePointIds) {
            jsonData.visiblePointIds = jsonData.visiblePointIds.map(visiblePointId => `${visiblePointId}`)
        }
        return jsonData
    }

}


function createSticky(options = {
    element,
    containerEl,
}) {
    options = {
        position: 'bottom',
        ...options
    }
    const { containerEl, element } = options
    if (!containerEl && !element) {
        console.error('Element and containerEl not set.')
        return null
    }

    const parentEl = element.parentElement

    if (containerEl === parentEl) {
        console.error('Element cannot be direct child of containerEl. Wrap your sticky element in another element.')
        return null
    }

    let documentStart = 0
    let documentEnd = 0
    let positionOffset = 0 //adjusted based on position
    let left = 0
    let isSticky = false
    let isEndPosition = false

    const watch = () => {
        resize()
        Scroll.on("scroll", handleScroll)
        Scroll.on("resize", handleResize)
    }

    const handleScroll = () => {
        update()
    }

    const handleResize = () => {
        resize()
    }

    const clear = () => {
        parentEl.style.width = ``
        parentEl.style.height = ``
        element.style.position = `relative`
        element.style.width = ``
        element.style.top = ``
        element.style.left = ``
        isEndPosition = false
        isSticky = false
    }

    const update = () => {
        const scrollY = Scroll.getScroll().y
        if (scrollY > documentEnd + positionOffset) {
            if (!isEndPosition) {
                element.classList.remove('is-sticky')
                element.style.position = 'relative'
                element.style.top = `${documentEnd - documentStart}px`
                element.style.left = ``
                isSticky = false
                isEndPosition = true
            }
        } else if (scrollY > documentStart + positionOffset) {
            element.style.position = 'fixed'
            if (!isSticky) {
                element.classList.add('is-sticky')
                element.style.top = `${-positionOffset}px` //@todo positionOffset
                element.style.left = `${left}px`
                isEndPosition = false
                isSticky = true
            }
        } else {
            if (isSticky) {
                element.classList.remove('is-sticky')
                element.style.position = 'relative'
                element.style.top = ``
                element.style.left = ``
                isEndPosition = false
                isSticky = false
            }
        }
    }

    const resize = () => {
        clear()
        const scrollY = Scroll.getScroll().y
        const containerRect = containerEl.getBoundingClientRect()
        const rect = element.getBoundingClientRect()
        const HARDEDCODED_OFFSET = 10 //aby to nebylo nalepeny uplne na spodni hranu, ale 10px od

        documentStart = scrollY + rect.top
        documentEnd = scrollY + containerRect.top + containerRect.height - rect.height
        positionOffset = -(Scroll.window.height - rect.height) + HARDEDCODED_OFFSET
        left = rect.left
        parentEl.style.width = `${rect.width}px`
        parentEl.style.height = `${rect.height}px`
        element.style.width = `${rect.width}px`

        update()
    }

    const destroy = () => {
        Scroll.off("scroll", handleScroll)
        Scroll.off("resize-x", handleResize)
    }

    return {
        watch,
        update,
        getState() {
            return {
                isEndPosition,
                isSticky,
                left,
                positionOffset,
                documentEnd,
                documentStart,
            }
        },
        resize,
        destroy,
    }
}